import React, { Fragment, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'reactstrap';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';
import { DATA_PRIVACY_FORM_FIELDS } from 'site-modules/data-privacy-request/constants/constants';
import { DataPrivacyFormField } from 'site-modules/data-privacy-request/components/data-privacy-form-field/data-privacy-form-field';
import { FormFieldMask } from 'site-modules/shared/components/form-field-mask/form-field-mask';
import { PHONE_MASK_V2 } from 'site-modules/shared/constants/lead-form/validation';

export const DataPrivacyRequestAgentFieldSet = ({
  validationErrors = {},
  fieldRef,
  onValidateField,
  stateCodes = [],
}) => {
  const [agentState, setAgentState] = useState();

  const handleAgentStateChange = useCallback(selectedAgentState => {
    setAgentState(selectedAgentState.stateCode);
  }, []);

  return (
    <Fragment>
      <hr />
      <fieldset>
        <legend className="visually-hidden">Agent&apos;s info</legend>
        <div className="medium mb-1 ps-0_25">Asterisks (*) indicate required fields.</div>
        <DataPrivacyFormField
          id={DATA_PRIVACY_FORM_FIELDS.AGENT_EMAIL}
          maxLength={255}
          name={DATA_PRIVACY_FORM_FIELDS.AGENT_EMAIL}
          size={20}
          type="email"
          placeholder="Email Address"
          label="Agent's Email Address*"
          labelClassName="ps-0_25"
          fieldRef={fieldRef}
          isValid={!(DATA_PRIVACY_FORM_FIELDS.AGENT_EMAIL in validationErrors)}
          onBlur={onValidateField}
          required
          error={validationErrors[DATA_PRIVACY_FORM_FIELDS.AGENT_EMAIL]}
          autoComplete="email"
        />
        <Row>
          <Col xs={6}>
            <DataPrivacyFormField
              id={DATA_PRIVACY_FORM_FIELDS.AGENT_FIRST_NAME}
              maxLength={35}
              name={DATA_PRIVACY_FORM_FIELDS.AGENT_FIRST_NAME}
              size={20}
              type="text"
              placeholder="First Name"
              label="Agent's First Name*"
              labelClassName="ps-0_25"
              fieldRef={fieldRef}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.AGENT_FIRST_NAME in validationErrors)}
              onBlur={onValidateField}
              required
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.AGENT_FIRST_NAME]}
              autoComplete="given-name"
            />
          </Col>
          <Col xs={6}>
            <DataPrivacyFormField
              id={DATA_PRIVACY_FORM_FIELDS.AGENT_LAST_NAME}
              maxLength={35}
              name={DATA_PRIVACY_FORM_FIELDS.AGENT_LAST_NAME}
              size={20}
              type="text"
              placeholder="Last Name"
              label="Agent's Last Name*"
              labelClassName="ps-0_25"
              fieldRef={fieldRef}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.AGENT_LAST_NAME in validationErrors)}
              onBlur={onValidateField}
              required
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.AGENT_LAST_NAME]}
              autoComplete="family-name"
            />
          </Col>
        </Row>
        <DataPrivacyFormField
          component={FormFieldMask}
          id={DATA_PRIVACY_FORM_FIELDS.AGENT_PHONE}
          name={DATA_PRIVACY_FORM_FIELDS.AGENT_PHONE}
          type="tel"
          label="Agent’s Phone Number*"
          labelClassName="ps-0_25"
          fieldRef={fieldRef}
          isValid={!(DATA_PRIVACY_FORM_FIELDS.AGENT_PHONE in validationErrors)}
          onBlur={onValidateField}
          required
          mask={{ ...PHONE_MASK_V2, placeholder: 'Phone Number' }}
          error={validationErrors[DATA_PRIVACY_FORM_FIELDS.AGENT_PHONE]}
          autoComplete="tel"
        />
        <DataPrivacyFormField
          id={DATA_PRIVACY_FORM_FIELDS.AGENT_ADDRESS}
          maxLength={90}
          name={DATA_PRIVACY_FORM_FIELDS.AGENT_ADDRESS}
          size={20}
          type="text"
          placeholder="Street Address"
          label="Agent’s Street Address*"
          labelClassName="ps-0_25"
          fieldRef={fieldRef}
          isValid={!(DATA_PRIVACY_FORM_FIELDS.AGENT_ADDRESS in validationErrors)}
          onBlur={onValidateField}
          required
          error={validationErrors[DATA_PRIVACY_FORM_FIELDS.AGENT_ADDRESS]}
          autoComplete="street-address"
        />
        <Row>
          <Col xs={8}>
            <DataPrivacyFormField
              id={DATA_PRIVACY_FORM_FIELDS.AGENT_CITY}
              maxLength={45}
              name={DATA_PRIVACY_FORM_FIELDS.AGENT_CITY}
              size={20}
              type="text"
              placeholder="City"
              label="Agent’s City*"
              labelClassName="ps-0_25"
              fieldRef={fieldRef}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.AGENT_CITY in validationErrors)}
              onBlur={onValidateField}
              required
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.AGENT_CITY]}
              autoComplete="address-level2"
            />
          </Col>
          <Col xs={4}>
            <DataPrivacyFormField
              value={agentState}
              options={stateCodes}
              onChange={handleAgentStateChange}
              id={DATA_PRIVACY_FORM_FIELDS.AGENT_STATE}
              name={DATA_PRIVACY_FORM_FIELDS.AGENT_STATE}
              valueKey="stateCode"
              labelKey="stateCode"
              labelText="Agent’s State*"
              labelClassName="ps-0_25"
              fieldRef={fieldRef}
              tag={StyledSelect}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.AGENT_STATE in validationErrors)}
              isFirstOptionDisabled
              toggle="Select State"
              required
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.AGENT_STATE]}
              onBlur={onValidateField}
              formControlColor="text-gray-darkest"
              autoComplete="address-level1"
            />
          </Col>
        </Row>
        <Row>
          <Col xs={8}>
            <DataPrivacyFormField
              id={DATA_PRIVACY_FORM_FIELDS.AGENT_ZIP}
              maxLength={10}
              name={DATA_PRIVACY_FORM_FIELDS.AGENT_ZIP}
              size={20}
              type="text"
              placeholder="Zip Code"
              label="Agent’s Zip Code*"
              labelClassName="ps-0_25"
              fieldRef={fieldRef}
              isValid={!(DATA_PRIVACY_FORM_FIELDS.AGENT_ZIP in validationErrors)}
              onBlur={onValidateField}
              required
              error={validationErrors[DATA_PRIVACY_FORM_FIELDS.AGENT_ZIP]}
              autoComplete="postal-code"
            />
          </Col>
        </Row>
      </fieldset>
    </Fragment>
  );
};

DataPrivacyRequestAgentFieldSet.propTypes = {
  validationErrors: PropTypes.shape({}),
  fieldRef: PropTypes.func.isRequired,
  onValidateField: PropTypes.func.isRequired,
  stateCodes: PropTypes.arrayOf(PropTypes.shape({ stateCode: PropTypes.string })),
};
